import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Home, Briefcase, Edit, Trash2, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import PreciseLocationSearch from '@/components/PreciseLocationSearch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { isLocationInAllowedZone } from '@/utils/zoneValidation';

interface SavedLocation {
  id: string;
  name: string;
  address_line: string;
  city: string;
  is_default: boolean;
  latitude?: number;
  longitude?: number;
  location_type: string;
}

const LocationManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [locations, setLocations] = useState<SavedLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingLocation, setEditingLocation] = useState<SavedLocation | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationType, setNewLocationType] = useState('other');

  useEffect(() => {
    if (user) {
      fetchLocations();
    }
  }, [user]);

  const fetchLocations = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('saved_locations')
        .select('*')
        .eq('user_id', user.id)
        .order('is_default', { ascending: false });

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast({
        title: "Error",
        description: "Failed to load saved locations",
        variant: "destructive",
      });
    }
  };

  const handleLocationSelect = async (locationData: { address: string; city: string; lat: number; lng: number }) => {
    if (!user) return;

    // Validate if location is in allowed zone
    if (!isLocationInAllowedZone(locationData.address, locationData.city)) {
      toast({
        title: "Location Not Supported",
        description: "Service is currently only available in Banjul and Western regions. Please select a location within these areas.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      if (editingLocation) {
        // Update existing location
        const { error } = await supabase
          .from('saved_locations')
          .update({
            address_line: locationData.address,
            city: locationData.city,
            latitude: locationData.lat,
            longitude: locationData.lng,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingLocation.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Location updated successfully",
        });
        setEditingLocation(null);
      } else {
        // Add new location
        const { error } = await supabase
          .from('saved_locations')
          .insert({
            user_id: user.id,
            name: newLocationName || 'New Location',
            address_line: locationData.address,
            city: locationData.city,
            latitude: locationData.lat,
            longitude: locationData.lng,
            location_type: newLocationType,
            is_default: locations.length === 0
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Location added successfully",
        });
        setShowAddForm(false);
        setNewLocationName('');
        setNewLocationType('other');
      }

      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      toast({
        title: "Error",
        description: "Failed to save location",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const setDefaultLocation = async (locationId: string) => {
    if (!user) return;

    try {
      // Remove default from all locations
      await supabase
        .from('saved_locations')
        .update({ is_default: false })
        .eq('user_id', user.id);

      // Set new default
      const { error } = await supabase
        .from('saved_locations')
        .update({ is_default: true })
        .eq('id', locationId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Default location updated",
      });

      fetchLocations();
      
      // Trigger location update event
      window.dispatchEvent(new Event('locationUpdated'));
    } catch (error) {
      console.error('Error setting default location:', error);
      toast({
        title: "Error",
        description: "Failed to update default location",
        variant: "destructive",
      });
    }
  };

  const handleLocationCardClick = async (location: SavedLocation) => {
    if (!location.is_default) {
      await setDefaultLocation(location.id);
    }
  };

  const deleteLocation = async (locationId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('saved_locations')
        .delete()
        .eq('id', locationId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Location deleted successfully",
      });

      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast({
        title: "Error",
        description: "Failed to delete location",
        variant: "destructive",
      });
    }
  };

  const getLocationIcon = (type: string, isDefault: boolean) => {
    if (type === 'home') return <Home className={`h-5 w-5 ${isDefault ? 'text-red-600' : 'text-gray-600'}`} />;
    if (type === 'work') return <Briefcase className={`h-5 w-5 ${isDefault ? 'text-red-600' : 'text-gray-600'}`} />;
    return <MapPin className={`h-5 w-5 ${isDefault ? 'text-red-600' : 'text-gray-600'}`} />;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Saved Locations</h3>
        <Button onClick={() => setShowAddForm(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Location
        </Button>
      </div>

      {/* Home and Work locations in horizontal boxes */}
      <div className="grid grid-cols-2 gap-3">
        {['home', 'work'].map((type) => {
          const location = locations.find(loc => loc.location_type === type);
          return (
            <Card 
              key={type} 
              className={`cursor-pointer hover:shadow-md transition-shadow ${
                location?.is_default ? 'ring-2 ring-red-200 bg-red-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => location && handleLocationCardClick(location)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  {getLocationIcon(type, location?.is_default || false)}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium capitalize">{type}</p>
                    {location ? (
                      <>
                        <p className="text-sm text-gray-600 truncate">{location.address_line}</p>
                        {location.is_default && (
                          <span className="text-xs text-red-600 font-medium">Current</span>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-400">Not set</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (location) {
                        setEditingLocation(location);
                      } else {
                        setNewLocationType(type);
                        setNewLocationName(type === 'home' ? 'Home' : 'Work');
                        setShowAddForm(true);
                      }
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Other locations */}
      {locations.filter(loc => !['home', 'work'].includes(loc.location_type)).map((location) => (
        <Card key={location.id} className={`${location.is_default ? 'ring-2 ring-red-200' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                {getLocationIcon(location.location_type, location.is_default)}
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{location.name}</p>
                  <p className="text-sm text-gray-600 truncate">{location.address_line}</p>
                  {location.is_default && (
                    <span className="text-xs text-red-600 font-medium">Default</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!location.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDefaultLocation(location.id)}
                  >
                    Set Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingLocation(location)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteLocation(location.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Add/Edit Location Form */}
      {(showAddForm || editingLocation) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingLocation ? 'Edit Location' : 'Add New Location'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {showAddForm && (newLocationType === 'home' || newLocationType === 'work') && (
              <div>
                <Label htmlFor="locationName">Location Name</Label>
                <Input
                  id="locationName"
                  value={newLocationName}
                  onChange={(e) => setNewLocationName(e.target.value)}
                  placeholder="Enter location name"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Search Location</Label>
              <p className="text-sm text-gray-600">
                Service is only available in Banjul and Western regions
              </p>
              <PreciseLocationSearch
                onLocationSelect={handleLocationSelect}
                placeholder="Search for your location..."
                value={editingLocation?.address_line || ''}
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingLocation(null);
                  setNewLocationName('');
                  setNewLocationType('other');
                }}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LocationManagement;
